import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { ClickableDancerName } from './utils/ClickableNames';
import DancerThumbnail from './DancerThumbnail';
import './DancerThumbnail.css';

export default function SimpleDancerSearch() {
  const [allDancers, setAllDancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDancers();
  }, []);

  // Debounce search term to prevent excessive re-renders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  async function loadDancers() {
    try {
      setLoading(true);
      setError(null);
      
      console.log("🔍 Starting to load dancers...");
      
      // Just try to load from old structure first
      const compsRef = collection(db, "competitions");
      const snapshot = await getDocs(compsRef);
      
      console.log("🔍 Found", snapshot.size, "competitions");
      
      const dancerSet = new Set();
      
      snapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results && Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            if (result.dancerName) {
              dancerSet.add(result.dancerName);
            }
          });
        }
      });
      
      const dancerList = Array.from(dancerSet).sort();
      console.log("🔍 Found", dancerList.length, "unique dancers");
      console.log("🔍 First 10 dancers:", dancerList.slice(0, 10));
      
      setAllDancers(dancerList);
      
    } catch (err) {
      console.error("❌ Error loading dancers:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredDancers = debouncedSearchTerm.length >= 2 
    ? allDancers.filter(name => 
        name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      ).slice(0, 10)
    : [];

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>🔍 Dancer Search</h2>
        <p>Loading dancers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>🔍 Dancer Search</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>🔍 Dancer Search</h2>
      <p style={{ color: "#666", marginBottom: "2rem" }}>
        Search through {allDancers.length} dancers in our database. Type at least 2 characters to see suggestions.
      </p>
      
      <div style={{ 
        marginBottom: "2rem", 
        padding: "1rem", 
        backgroundColor: "#f5f5f5", 
        borderRadius: "8px"
      }}>
        <input
          type="text"
          placeholder="Search for a dancer by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '1rem',
            fontSize: '1.1rem',
            border: '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#0066cc'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
      </div>
      
      {debouncedSearchTerm.length >= 2 && (
        <div>
          <div style={{ marginBottom: "1rem", color: "#666" }}>
            <p>📊 Found {filteredDancers.length} dancers matching "{debouncedSearchTerm}"</p>
          </div>
          
          {filteredDancers.length > 0 ? (
            <div style={{ 
              backgroundColor: "white",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}>
              {filteredDancers.map((name) => (
                <div key={name} style={{ 
                  borderBottom: filteredDancers.indexOf(name) < filteredDancers.length - 1 ? "1px solid #eee" : "none"
                }}>
                  <div className="search-result-dancer">
                    <DancerThumbnail 
                      dancerName={name} 
                      size={44} 
                      showPlaceholder={true}
                    />
                    <div className="search-result-info">
                      <ClickableDancerName 
                        name={name}
                        style={{ 
                          color: '#333', 
                          textDecoration: 'none',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#0066cc';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#333';
                        }}
                      >
                        {name}
                      </ClickableDancerName>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: "center", 
              padding: "3rem", 
              color: "#666",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px"
            }}>
              <p>No dancers found matching "{searchTerm}"</p>
              <p style={{ fontSize: "0.9rem" }}>Try a different spelling or fewer characters</p>
            </div>
          )}
        </div>
      )}
      
      {searchTerm.length < 2 && searchTerm.length > 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: "2rem", 
          color: "#666",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px"
        }}>
          <p>Type at least 2 characters to start searching</p>
        </div>
      )}
    </div>
  );
}
